import { Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import { isErrorEventSeverity, SEVERITY_SELECT_OPTIONS, SeverityBadge } from './badges';
import { ErrorEventSeverity, SeverityGroup } from '../../../services/api';
import { SeverityScope } from './severityResolution';

/** Auswahl „alle Identifier" — Katalog ohne Gruppen-Filter. */
export const ALL_IDENTIFIERS_KEY = '__all__';

/** Auswahl „ohne Gruppe" — Arbeitsliste der noch nicht zugeordneten Identifier. */
export const UNGROUPED_KEY = '__ungrouped__';

/** Sentinel für „keine eigene Severity" — Radix-Select verträgt keinen leeren Wert. */
const INHERIT_VALUE = '__inherit__';

export interface SeverityGroupPanelProps {
  groups: SeverityGroup[];
  scope: SeverityScope;
  /** {@link ALL_IDENTIFIERS_KEY}, {@link UNGROUPED_KEY} oder eine Gruppen-ID. */
  selectedKey: string;
  onSelectKey: (key: string) => void;
  /** Mitgliederzahl je Gruppen-ID inklusive gepufferter Umzuordnungen. */
  memberCounts: Map<string, number>;
  /** Identifier ohne Gruppe, inklusive gepufferter Umzuordnungen. */
  ungroupedCount: number;
  /** Globale Severity je Gruppe — im Kunden-Geltungsbereich die geerbte Basis. */
  globalSeverityOf: (groupId: string) => ErrorEventSeverity | null;
  /** Severity im aktiven Geltungsbereich — `null`, wenn dort keine eigene Regel liegt. */
  scopedSeverityOf: (groupId: string) => ErrorEventSeverity | null;
  onSeverityChange: (groupId: string, severity: ErrorEventSeverity | null) => void;
  onCreateGroup: () => void;
  onEditGroup: (group: SeverityGroup) => void;
  onDeleteGroup: (group: SeverityGroup) => void;
}

/**
 * Master-Spalte des Severity-Tabs: Gruppenliste mit Auswahl, die den
 * Identifier-Katalog rechts filtert.
 *
 * Im globalen Geltungsbereich ist der Select die Default-Severity der Gruppe,
 * im Kunden-Geltungsbereich der Override über der (mit angezeigten) globalen
 * Severity.
 */
export function SeverityGroupPanel({
  groups,
  scope,
  selectedKey,
  onSelectKey,
  memberCounts,
  ungroupedCount,
  globalSeverityOf,
  scopedSeverityOf,
  onSeverityChange,
  onCreateGroup,
  onEditGroup,
  onDeleteGroup,
}: SeverityGroupPanelProps) {
  const isCustomerScope = scope === 'customer';
  const totalMembers = groups.reduce((sum, group) => sum + (memberCounts.get(group.id) ?? 0), 0);

  return (
    <Card>
      <CardHeader className="border-b pb-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>{isCustomerScope ? 'Gruppen für diesen Kunden' : 'Fehler-Gruppen'}</CardTitle>
            <CardDescription>
              {isCustomerScope
                ? 'Override setzen oder auf global zurücksetzen'
                : `${groups.length} Gruppen · ${totalMembers + ungroupedCount} Identifier`}
            </CardDescription>
          </div>
          {!isCustomerScope && (
            <Button size="sm" onClick={onCreateGroup}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Gruppe
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => onSelectKey(ALL_IDENTIFIERS_KEY)}
          className={cn(
            'flex w-full items-center justify-between gap-3 border-b border-l-[3px] border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50',
            selectedKey === ALL_IDENTIFIERS_KEY
              ? 'border-l-gray-900 bg-gray-100'
              : 'border-l-transparent'
          )}
        >
          <span>
            <span className="block text-sm font-medium text-gray-900">Alle Identifier</span>
            <span className="block text-xs text-gray-500">Katalog ohne Gruppen-Filter</span>
          </span>
          <Badge variant="outline">{totalMembers + ungroupedCount}</Badge>
        </button>

        {groups.map((group) => {
          const scopedSeverity = scopedSeverityOf(group.id);
          const globalSeverity = globalSeverityOf(group.id);
          const isSelected = selectedKey === group.id;

          return (
            <div
              key={group.id}
              className={cn(
                'group border-b border-l-[3px] border-gray-100 px-4 py-3 transition-colors',
                isSelected ? 'border-l-gray-900 bg-gray-100' : 'border-l-transparent hover:bg-gray-50',
                isCustomerScope && scopedSeverity !== null && !isSelected && 'bg-amber-50/70'
              )}
            >
              <button
                type="button"
                onClick={() => onSelectKey(group.id)}
                className="flex w-full items-start justify-between gap-3 text-left"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{group.name}</span>
                  {group.description && (
                    <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
                      {group.description}
                    </span>
                  )}
                </span>
                <span className="flex flex-none items-center gap-1.5">
                  {isCustomerScope && globalSeverity && (
                    <span className="opacity-60" title="Globale Severity">
                      <SeverityBadge severity={globalSeverity} />
                    </span>
                  )}
                  {!isCustomerScope &&
                    (globalSeverity ? (
                      <SeverityBadge severity={globalSeverity} />
                    ) : (
                      <Badge variant="outline">Rückfall</Badge>
                    ))}
                </span>
              </button>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="font-normal text-gray-500">
                  {memberCounts.get(group.id) ?? 0} Identifier
                </Badge>

                <Select
                  value={scopedSeverity ?? INHERIT_VALUE}
                  onValueChange={(value) =>
                    onSeverityChange(group.id, isErrorEventSeverity(value) ? value : null)
                  }
                >
                  <SelectTrigger
                    size="sm"
                    className={cn(
                      'h-7 min-w-[128px] text-xs',
                      scopedSeverity === null && 'border-dashed bg-gray-50 text-gray-400',
                      isCustomerScope && scopedSeverity !== null && 'border-amber-300'
                    )}
                    aria-label={
                      isCustomerScope
                        ? `Override für Gruppe ${group.name}`
                        : `Default-Severity für Gruppe ${group.name}`
                    }
                  >
                    <SelectValue placeholder={isCustomerScope ? '—' : 'Keine'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT_VALUE}>
                      {isCustomerScope ? '— erbt global' : 'Keine — Rückfallwert'}
                    </SelectItem>
                    {SEVERITY_SELECT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {isCustomerScope && scopedSeverity !== null && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-gray-500"
                    title="Auf globale Regel zurücksetzen"
                    onClick={() => onSeverityChange(group.id, null)}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                )}

                {!isCustomerScope && (
                  <span className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-500"
                      title="Gruppe bearbeiten"
                      onClick={() => onEditGroup(group)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-500 hover:text-red-600"
                      title="Gruppe löschen"
                      onClick={() => onDeleteGroup(group)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                )}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => onSelectKey(UNGROUPED_KEY)}
          className={cn(
            'flex w-full items-center justify-between gap-3 border-l-[3px] px-4 py-3 text-left transition-colors hover:bg-gray-50',
            selectedKey === UNGROUPED_KEY ? 'border-l-gray-900 bg-gray-100' : 'border-l-transparent'
          )}
        >
          <span>
            <span className="block text-sm font-medium text-gray-900">Ohne Gruppe</span>
            <span className="block text-xs text-gray-500">
              Noch nicht zugeordnet — landet beim Rückfallwert
            </span>
          </span>
          <Badge
            className={cn(
              ungroupedCount > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
            )}
          >
            {ungroupedCount}
          </Badge>
        </button>
      </CardContent>
    </Card>
  );
}
